import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { businessAPI } from '../../api/businesses';
import { useAuth } from '../../contexts/AuthContext';
import AddStaffModal from './AddStaffModal';
import LoadingSpinner from '../common/LoadingSpinner';
import { resolveImageUrl } from '../../utils/formatters';

const StaffList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [business, setBusiness] = useState(null);
  const [avatarErrors, setAvatarErrors] = useState({});

  useEffect(() => {
    loadBusinessAndStaff();
  }, []);

  const loadBusinessAndStaff = async () => {
    try {
      const businessRes = await businessAPI.getMyBusiness();
      const businessData = businessRes?.data?.data || null;
      setBusiness(businessData);

      if (!businessData?.id) {
        setStaff([]);
        return;
      }
      
      const staffRes = await businessAPI.getStaff(businessData.id);
      setStaff(staffRes.data.data);
    } catch (error) {
      console.error('Failed to load staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStaff = (staffMember) => {
    setEditingStaff(staffMember);
    setShowAddModal(true);
  };

  const handleRemoveStaff = async (staffId) => {
    if (window.confirm('Are you sure you want to remove this staff member?')) {
      try {
        await businessAPI.removeStaff(business.id, staffId);
        await loadBusinessAndStaff();
        alert('Staff member removed successfully');
      } catch (error) {
        console.error('Failed to remove staff:', error);
      }
    }
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingStaff(null);
  };

  if (loading) return <LoadingSpinner />;

  if (!business?.id) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Staff Management</h1>
            <p className="text-gray-600">Manage your team members</p>
          </div>
          <button
            onClick={() => navigate('/business/setup')}
            className="btn-primary"
          >
            Create Business
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-700 font-medium">No business found for this account.</p>
          <p className="text-sm text-gray-500 mt-2">Create your business first, then you can add staff members.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Staff Management</h1>
          <p className="text-gray-600">Manage your team members</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          Add Staff Member
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {staff.map((member) => (
              <tr key={member.id}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {member.profile_image && !avatarErrors[member.id] ? (
                      <img
                        src={resolveImageUrl(member.profile_image)}
                        alt={`${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Staff'}
                        className="h-8 w-8 rounded-full object-cover border"
                        onError={() => setAvatarErrors((prev) => ({ ...prev, [member.id]: true }))}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full border bg-gray-50 flex items-center justify-center text-xs font-bold text-gray-500">
                        {(member.first_name || 'S').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <span>{member.first_name} {member.last_name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{member.email}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{member.position}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{member.phone}</td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button
                    onClick={() => handleEditStaff(member)}
                    className="text-blue-600 hover:text-blue-800 mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleRemoveStaff(member.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <AddStaffModal
          businessId={business.id}
          onClose={handleModalClose}
          staffMember={editingStaff}
          onSuccess={() => {
            handleModalClose();
            loadBusinessAndStaff();
          }}
        />
      )}
    </div>
  );
};

export default StaffList;
